import React, { useRef, useState } from "react";
import { extractYouTubeId } from "../../lib/youtube";

export default function TrailerSection({ t, refEl }) {
  const src = String(t.hero_video || "").trim();
  const isFile = /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(src);
  const ytId = !isFile ? extractYouTubeId(src) : "";
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  if (!isFile && !ytId) return null;

  const play = () => {
    setPlaying(true);
    const v = videoRef.current;
    if (v) { v.muted = false; v.volume = 1; v.play().catch(() => {}); }
  };

  return (
    <section
      id="trailer"
      ref={refEl}
      data-testid="reality-trailer"
      className="relative bg-[#0B0B0C] py-16 md:py-24 border-t border-[#F1EDE6]/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.32em] text-[color:var(--alta-red-soft)]">
              <span className="rec-dot" aria-hidden="true" />
              {t.trailer_label}
            </div>
            <h2 className="mt-4 condensed text-[44px] md:text-[64px] text-[#F1EDE6] leading-[0.9] max-w-[22ch]">{t.trailer_title}</h2>
          </div>
          {t.trailer_youtube_url && (
            <a
              href={t.trailer_youtube_url}
              target="_blank"
              rel="noreferrer"
              data-testid="trailer-youtube-link"
              className="mono text-[10px] uppercase tracking-[0.28em] text-[#F1EDE6]/70 hover:text-[color:var(--alta-red-soft)] transition-colors border-b border-[#F1EDE6]/30 pb-1 self-start md:self-auto whitespace-nowrap"
            >
              {t.trailer_youtube_label} →
            </a>
          )}
        </div>

        <div className="relative aspect-video bg-black border border-[#F1EDE6]/15 overflow-hidden scanlines">
          <div className="frame-mark tl" /><div className="frame-mark tr" />
          <div className="frame-mark bl" /><div className="frame-mark br" />
          {isFile ? (
            <>
              <video
                ref={videoRef}
                data-testid="trailer-video"
                src={`${src}#t=0.5`}
                poster={t.trailer_poster || undefined}
                className="absolute inset-0 w-full h-full object-cover bg-black"
                controls={playing}
                playsInline
                preload="metadata"
                onEnded={() => setPlaying(false)}
              />
              {!playing && (
                <button
                  type="button"
                  onClick={play}
                  data-testid="trailer-play"
                  aria-label={t.cta_trailer}
                  className="absolute inset-0 group flex items-center justify-center bg-[#0B0B0C]/40 hover:bg-[#0B0B0C]/25 transition-colors"
                >
                  <span className="flex flex-col items-center gap-5">
                    <span className="w-24 h-24 md:w-28 md:h-28 rounded-full border border-[#F1EDE6]/60 bg-[#0B0B0C]/50 backdrop-blur flex items-center justify-center transition-transform duration-500 group-hover:scale-110 group-hover:border-[color:var(--alta-red-soft)]">
                      <span className="ml-2 border-y-[16px] border-y-transparent border-l-[26px] border-l-[#F1EDE6]" />
                    </span>
                    <span className="mono text-[10px] uppercase tracking-[0.32em] text-[#F1EDE6]">{t.cta_trailer}</span>
                  </span>
                </button>
              )}
            </>
          ) : (
            <iframe
              data-testid="trailer-youtube"
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0`}
              title="Trailer Casa Alta"
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </section>
  );
}
